// First-party attribution: visitor id, UTMs / click ids captured on landing and
// kept for 30 days so they survive OTP sign-up, sign-in and payment redirects.
// Nothing here is personal data.
const VID_KEY = 'founderos:vid';
const ATTR_KEY = 'founderos:attribution';
const TTL_MS = 30 * 24 * 3600 * 1000;
export const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'gclid'];
const SENSITIVE = new Set(['token', 'code', 'otp', 'email', 'phone', 'access_token', 'refresh_token', 'session_id',
  'razorpay_payment_id', 'razorpay_order_id', 'razorpay_signature', 'sig', 'key', 'password']);
const SENSITIVE_PATH = /^\/(auth|reset|verify)(\/|$)/;

const store = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* private mode */ } };
const load = (k) => { try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch { return null; } };

export const getVisitorId = () => {
  let v = load(VID_KEY);
  if (!v) {
    v = (window.crypto?.randomUUID?.() || `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`);
    store(VID_KEY, v);
  }
  return v;
};

/** True when the URL carries a token / OTP / contact detail (analytics must skip it). */
export const hasSensitiveParams = (url) => {
  try {
    const u = new URL(url || '', window.location.origin);
    if (SENSITIVE_PATH.test(u.pathname)) return true;
    return [...u.searchParams.keys()].some((k) => SENSITIVE.has(k.toLowerCase()));
  } catch { return false; }
};

/** Strip OTP codes / tokens / contact details from a URL or path before it reaches analytics. */
export const sanitizeUrl = (url) => {
  if (!url) return '';
  try {
    const abs = /^https?:/i.test(url);
    const u = new URL(url, window.location.origin);
    if (SENSITIVE_PATH.test(u.pathname)) return abs ? `${u.origin}${u.pathname}` : u.pathname;
    [...u.searchParams.keys()].forEach((k) => { if (SENSITIVE.has(k.toLowerCase())) u.searchParams.delete(k); });
    u.hash = '';
    return abs ? u.toString() : `${u.pathname}${u.search}`;
  } catch { return ''; }
};

/** Remember UTMs / click ids from the current URL (last-touch wins, first-touch kept). */
export const captureAttribution = () => {
  isMarketingVisit();
  const sp = new URLSearchParams(window.location.search);
  const found = {};
  UTM_KEYS.forEach((k) => { const v = sp.get(k); if (v) found[k] = v.slice(0, 120); });
  const now = Date.now();
  const prev = load(ATTR_KEY);
  const fresh = prev && now - (prev.at || 0) < TTL_MS ? prev : null;
  if (!Object.keys(found).length) return fresh?.last || {};
  const next = {
    at: now,
    first: fresh?.first || { ...found, landing: sanitizeUrl(window.location.href), at: now },
    last: { ...found, landing: sanitizeUrl(window.location.href), at: now },
  };
  store(ATTR_KEY, next);
  return next.last;
};

/** Last-touch UTMs (+ click ids) if captured in the last 30 days. */
export const getAttribution = () => {
  const a = load(ATTR_KEY);
  if (!a || Date.now() - (a.at || 0) > TTL_MS) return {};
  const out = {};
  UTM_KEYS.forEach((k) => { if (a.last?.[k]) out[k] = a.last[k]; });
  return out;
};

/** First landing URL captured on a campaign visit in the last 30 days
 *  (already sanitized, no PII). '' when none was captured. */
export const getLandingUrl = () => {
  const a = load(ATTR_KEY);
  if (!a || Date.now() - (a.at || 0) > TTL_MS) return '';
  return a.first?.landing || a.last?.landing || '';
};

export const isMobileViewport = () => window.matchMedia?.('(max-width: 767px)')?.matches || false;

// Campaign visit (UTM / click id / coupon link) — remembered for the browser session so
// distractions (app-install banner, "Become a mentor") stay hidden until they've booked.
const MKT_KEY = 'founderos:mkt-visit';
export const MARKETING_KEYS = [...UTM_KEYS, 'coupon'];
export const isMarketingVisit = () => {
  try {
    if (sessionStorage.getItem(MKT_KEY) === '1') return true;
    const sp = new URLSearchParams(window.location.search);
    if (MARKETING_KEYS.some((k) => sp.get(k))) { sessionStorage.setItem(MKT_KEY, '1'); return true; }
  } catch { /* private mode */ }
  return false;
};
